import { useCoworkAuth } from "@/cowork/auth"
import { useCoworkWorkspace, CoworkWorkspaceProvider } from "@/cowork/workspace-context"
import { useLanguage } from "@/context/language"
import { CoworkBrand } from "@/cowork/brand"
import { CoworkChatDock } from "@/components/cowork-chat-dock"
import { useGlobal } from "@/context/global"
import { isChatDockPath, localWorkspacePath } from "@/cowork/chat-dock"
import { filesImportNavigationEvent, filesImportPath } from "@/cowork/files-import-navigation"
import { createMediaQuery } from "@solid-primitives/media"
import { accountingNavigationArea } from "@/cowork/accounting-navigation"
import { tabHref, tabKey, useTabs, type SessionTab } from "@/context/tabs"
import { ServerConnection, useServer } from "@/context/server"
import { useSettingsCommand } from "@/components/settings-dialog"
import { CoworkChatUnavailableError, openCoworkChat } from "@/cowork/open-chat"
import { createCoworkChatCloser } from "@/cowork/close-chat"
import { reopenCoworkChat } from "@/cowork/reopen-chat"
import { showToast } from "@/utils/toast"
import { collectionPath, collectionRoute, scopedCollections } from "@/cowork/scope-url"
import { formatDate } from "@/utils/date"
import { resolveNewChatScope } from "@/cowork/new-chat-scope"
import { useBeforeLeave, useLocation } from "@solidjs/router"
import { appPath, useAppNavigate } from "@/cowork/workspace-route"
import { createEffect, createMemo, createResource, For, onCleanup, Show, Suspense, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import {
  listPortfolioChats,
  listPortfolioFiles,
  type CoworkPortfolioChat,
  type CoworkPortfolioFile,
  getMe,
} from "@/cowork/api"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { Portal } from "solid-js/web"

type NavIconName =
  | "dashboard"
  | "tiles"
  | "company"
  | "project"
  | "chat"
  | "file"
  | "folder"
  | "report"
  | "template"
  | "ledger"
  | "agent"
  | "library"
  | "workflow"
  | "approval"
  | "activity"
  | "integration"
  | "audit"
  | "settings"

type NavItem = {
  label: keyof typeof import("@/i18n/en").dict
  route: string
  icon: NavIconName
}

type NavSection = {
  label?: keyof typeof import("@/i18n/en").dict
  items: NavItem[]
}

type HeaderSearchResult =
  | { type: "chats"; item: CoworkPortfolioChat; activity: number }
  | { type: "files"; item: CoworkPortfolioFile; activity: number }

const platformSection: NavSection = {
  label: "cowork.enterprise.nav.platform",
  items: [
    { label: "cowork.enterprise.nav.accessRequests", route: "/admin/access-requests", icon: "approval" },
    { label: "cowork.enterprise.nav.adminWorkspaces", route: "/admin/workspaces", icon: "company" },
    { label: "cowork.manageUsers.title", route: "/admin/manage-users", icon: "company" },
  ],
}

const sections: NavSection[] = [
  { items: [
    { label: "accounting.overview.nav", route: "/dashboard", icon: "dashboard" },
    { label: "cowork.navigation.files", route: "/files", icon: "folder" },
    { label: "billing.nav", route: "/billing", icon: "file" },
    { label: "books.nav", route: "/books", icon: "ledger" },
    { label: "cowork.enterprise.nav.reports", route: "/reports", icon: "report" },
    { label: "dashboard.title", route: "/dashboards", icon: "tiles" },
    { label: "cowork.usage.approvals", route: "/approvals", icon: "approval" },
    { label: "cowork.accountingNav.personnel", route: "/personnel", icon: "company" },
    { label: "cowork.enterprise.nav.settings", route: "/settings", icon: "settings" },
  ] },
]

const scopeSwitcherCollapsedKey = "cowork.enterprise.scopeSwitcherCollapsed"

export function CoworkEnterpriseShell(props: { children: JSX.Element }) {
  return (
    <CoworkWorkspaceProvider>
      <CoworkEnterpriseShellContent>{props.children}</CoworkEnterpriseShellContent>
    </CoworkWorkspaceProvider>
  )
}

function CoworkEnterpriseShellContent(props: { children: JSX.Element }) {
  const global = useGlobal()
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const availableProjects = createMemo(() =>
    context.projects.state === "ready" || context.projects.state === "refreshing"
      ? (context.projects.latest ?? []).filter((project) => project.company_id === context.state.entityId)
      : [],
  )
  const availablePeriods = createMemo(() =>
    context.periods.state === "ready" || context.periods.state === "refreshing"
      ? (context.periods.latest ?? []).filter(
          (period) => period.company_id === context.state.entityId && period.project_id === context.state.projectId,
        )
      : [],
  )
  const auth = useCoworkAuth()
  const server = useServer()
  const tabs = useTabs()
  const [me] = createResource(() => getMe().catch(() => undefined))
  const visibleSections = createMemo(() => (me()?.platform_admin ? [...sections, platformSection] : sections))
  const [chatIndex] = createResource(() => listPortfolioChats())
  useSettingsCommand()
  const location = useLocation()
  const navigate = useAppNavigate()
  const mobileDock = createMediaQuery("(max-width: 900px)")
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const [state, setState] = createStore({
    sidebarCollapsed: /^\/reports\/[^/]+\/[^/]+\/edit\/?$/.test(appPath(location.pathname)),
    mobileNavigationOpen: false,
    scopeSwitcherCollapsed: storedScopeSwitcherCollapsed(),
    searchOpen: false,
    searchQuery: "",
    searchType: "all" as "all" | "chats" | "files",
    searchOpening: "",
    searchError: "",
    chatOpen: false,
    chatExpanded: false,
    chatPath: "",
    chatScope: "",
    reconnectingPath: "",
    reconnectError: false,
    workspacePath: isChatDockPath(location.pathname) ? "/dashboard" : location.pathname + location.search,
  })
  const dockScope = () => JSON.stringify([context.state.entityId, context.state.projectId, context.state.periodId])
  let reconnectVersion = 0
  createEffect(() => {
    state.chatPath; state.chatOpen; dockScope()
    reconnectVersion++
    setState({ reconnectingPath: "", reconnectError: false })
  })
  const openDock = (path: string) => setState({ chatOpen: true, chatPath: path, chatScope: dockScope() })
  const resetDock = () => setState({ chatOpen: false, chatPath: "", chatExpanded: false, chatScope: "" })
  const closeDock = () => {
    setState("chatOpen", false)
    document.querySelector<HTMLButtonElement>("[data-chat-dock-toggle]")?.focus()
  }
  useBeforeLeave((event) => {
    if (typeof event.to === "string" && isChatDockPath(event.to)) {
      event.preventDefault()
      openDock(event.to)
    } else if (state.chatPath && state.chatScope !== dockScope()) resetDock()
  })
  createEffect(() => {
    const path = location.pathname + location.search
    // Direct links and browser history can arrive without a cancellable navigation.
    if (isChatDockPath(path)) {
      openDock(path)
      navigate(state.workspacePath, { replace: true })
    } else if (localWorkspacePath(path)) setState("workspacePath", path)
  })
  const [fileIndex] = createResource(
    () => state.searchOpen,
    () => listPortfolioFiles(),
  )

  const workspaceName = context.workspaceName
  const entityLabel = createMemo(
    () => context.selectedEntity()?.name ?? language.t("cowork.enterprise.scope.selectCompany"),
  )
  const projectLabel = createMemo(
    () => context.selectedProject()?.name ?? language.t("cowork.enterprise.scope.selectProject"),
  )
  const periodLabel = createMemo(() => {
    const period = context.selectedPeriod()
    return period ? formatDate(period.name) : language.t("cowork.enterprise.scope.allPeriods")
  })
  const scopeSummary = createMemo(() =>
    [
      entityLabel(),
      context.state.projectId ? projectLabel() : undefined,
      context.supportsPeriods() && context.state.periodId ? periodLabel() : undefined,
    ]
      .filter(Boolean)
      .join(" / "),
  )
  const accountName = createMemo(
    () => auth.state.accountName || auth.state.accountEmail || language.t("cowork.enterprise.profile.user"),
  )
  const chatTabs = createMemo(() => tabs.store.filter((tab): tab is SessionTab => tab.type === "session"))
  const tabWorking = (tab: SessionTab) => {
    const connection = global.servers.list().find((item) => ServerConnection.key(item) === tab.server)
    return connection ? global.ensureServerCtx(connection).sync.session.data.session_working(tab.sessionId) : false
  }
  const searchResults = createMemo<HeaderSearchResult[]>(() => {
    if (!state.searchOpen) return []
    const query = state.searchQuery.trim().toLowerCase()
    const chats = (chatIndex() ?? [])
      .filter((chat) =>
        `${chat.title} ${chat.company_name} ${chat.project_name} ${chat.period_name ?? chat.workflow_name ?? ""}`
          .toLowerCase()
          .includes(query),
      )
      .map((item) => ({ type: "chats" as const, item, activity: chatActivity(item) }))
    const files = (fileIndex.loading ? [] : (fileIndex() ?? []))
      .filter((file) =>
        `${file.filename} ${file.company_name} ${file.project_name} ${file.period_name ?? ""} ${file.content_type}`
          .toLowerCase()
          .includes(query),
      )
      .map((item) => ({ type: "files" as const, item, activity: fileActivity(item) }))
    return [...(state.searchType === "files" ? [] : chats), ...(state.searchType === "chats" ? [] : files)]
      .sort((left, right) => right.activity - left.activity)
      .slice(0, 50)
  })
  createEffect(() => {
    if (!state.searchOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      setState("searchOpen", false)
    }
    document.addEventListener("keydown", onKeyDown)
    onCleanup(() => document.removeEventListener("keydown", onKeyDown))
  })
  createEffect(() => {
    const chats = chatIndex()
    if (!chats) return
    for (const tab of chatTabs()) {
      const chat = chats.find((item) => item.remote_opencode_session_id === tab.sessionId)
      if (!chat) continue
      tabs.rememberCoworkContext(tab, {
        companyID: chat.company_id,
        projectID: chat.project_id,
        periodID: chat.period_id ?? chat.workflow_id ?? context.allPeriodsID,
        title: chat.title,
      })
    }
  })
  const initials = createMemo(
    () =>
      accountName()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "SC",
  )

  const currentPath = () => appPath(location.pathname)
  const companyDetail = () => currentPath().startsWith("/companies/")
  const active = (route: string) => accountingNavigationArea(currentPath()) === route

  const openRoute = (route: string) => {
    setState({ mobileNavigationOpen: false, chatExpanded: false })
    const page = route.slice(1)
    if (scopedCollections.has(page)) {
      navigate(collectionPath(page, {
        companyID: context.state.entityId || undefined,
        projectID: page === "projects" ? undefined : context.state.projectId || undefined,
        periodID: context.state.periodId === context.allPeriodsID ? undefined : context.state.periodId || undefined,
      }))
      return
    }
    navigate(route)
  }

  const openFilesImport = () => {
    if (mobileDock()) setState("chatOpen", false)
    openRoute(filesImportPath({
      companyID: context.state.entityId || undefined,
      projectID: context.state.projectId || undefined,
      periodID: context.state.periodId === context.allPeriodsID ? undefined : context.state.periodId || undefined,
    }))
  }
  window.addEventListener(filesImportNavigationEvent, openFilesImport)
  onCleanup(() => window.removeEventListener(filesImportNavigationEvent, openFilesImport))

  const startChat = () => {
    const activeTab = state.chatOpen ? chatTabs().find((tab) => tabHref(tab) === state.chatPath) : undefined
    const scope = resolveNewChatScope(context.state, activeTab ? tabs.info[tabKey(activeTab)] : undefined)
    if (!scope) {
      resetDock()
      navigate(collectionPath("projects", { companyID: context.state.entityId || undefined }))
      return
    }
    try {
      localStorage.setItem(
        "cowork.enterprise.agentContext",
        JSON.stringify({
          workspaceId: context.state.workspaceId,
          entityId: scope.companyID,
          projectId: scope.projectID,
          periodId: scope.periodID || null,
          page: location.pathname,
        }),
      )
    } catch {
      // Navigation still works when browser storage is unavailable.
    }
    const query = new URLSearchParams({ company: scope.companyID, project: scope.projectID })
    if (scope.periodID) query.set("period", scope.periodID)
    openDock(`/new-session?${query}`)
  }

  const toggleScopeSwitchers = () => {
    const collapsed = !state.scopeSwitcherCollapsed
    setState("scopeSwitcherCollapsed", collapsed)
    try {
      localStorage.setItem(scopeSwitcherCollapsedKey, String(collapsed))
    } catch {
      // The control still works when browser storage is unavailable.
    }
  }

  const selectChatTab = (tab: SessionTab) => {
    tabs.select(tab)
  }
  const closeChat = createCoworkChatCloser({
    global,
    tabs,
    isCurrent: () => !disposed,
    beforeClose: (tab) => {
      if (state.chatPath !== tabHref(tab)) return
      const chats = chatTabs(), position = chats.findIndex((item) => tabKey(item) === tabKey(tab))
      const next = chats[position + 1] ?? chats[position - 1]
      if (next) {
        // Unmount the closing session before its tab can be removed and re-registered.
        setState("chatPath", tabHref(next))
        selectChatTab(next)
      } else resetDock()
    },
  })
  const closeChatTab = (path: string) => {
    const tab = chatTabs().find((item) => tabHref(item) === path)
    if (!tab) return
    void closeChat(tab).catch(() => {
      if (disposed) return
      showToast({ variant: "error", title: language.t("common.requestFailed") })
    })
  }
  const reconnectChatTab = async (path: string) => {
    if (state.reconnectingPath || !state.chatOpen || state.chatPath !== path) return
    const tab = chatTabs().find((item) => tabHref(item) === path)
    if (!tab) return
    const info = tabs.info[tabKey(tab)]
    const scope = { companyID: info?.companyID || context.state.entityId, projectID: info?.projectID || context.state.projectId }
    const version = reconnectVersion
    const isCurrent = () => !disposed && version === reconnectVersion && state.chatOpen && state.chatPath === path
    setState({ reconnectingPath: path, reconnectError: false })
    try {
      await reopenCoworkChat(tab, scope, { context, server, tabs, isCurrent })
    } catch {
      if (isCurrent()) setState("reconnectError", true)
    } finally {
      if (isCurrent()) setState("reconnectingPath", "")
    }
  }
  const openSearchChat = async (chat: CoworkPortfolioChat) => {
    if (state.searchOpening) return
    setState({ searchOpening: chat.id, searchError: "" })
    const pathname = location.pathname
    const isCurrent = () => !disposed && state.searchOpen && location.pathname === pathname
    try {
      const opened = await openCoworkChat(
        {
          companyID: chat.company_id,
          projectID: chat.project_id,
          periodID: chat.period_id ?? chat.workflow_id,
          sessionID: chat.id,
          remoteSessionID: chat.remote_opencode_session_id,
          title: chat.title,
        },
        { context, server, tabs, isCurrent },
      )
      if (opened && !disposed) setState({ searchOpen: false, searchQuery: "" })
    } catch (error) {
      if (isCurrent())
        setState(
          "searchError",
          error instanceof CoworkChatUnavailableError
            ? language.t("cowork.enterprise.chats.unavailable")
            : error instanceof Error
              ? error.message
              : String(error),
        )
    } finally {
      if (!disposed) setState("searchOpening", "")
    }
  }

  const openSearchFile = (file: CoworkPortfolioFile) => {
    const periodID = file.period_id ?? file.workflow_id
    setState({ searchOpen: false, searchQuery: "" })
    navigate(collectionPath("files", { companyID: file.company_id, projectID: file.project_id, periodID: periodID ?? undefined }))
  }

  const selectEntity = (entityId: string) => {
    if (entityId !== context.state.entityId) resetDock()
    context.selectEntity(entityId)
    const route = collectionRoute(location.pathname, location.search)
    if (route) {
      navigate(route.page === "periods" && (!entityId || entityId === context.allEntitiesID)
        ? collectionPath("projects")
        : collectionPath(route.page, { companyID: entityId || undefined }))
      return
    }
    const parts = currentPath().split("/").filter(Boolean)
    if (!parts[0] || parts[0] === "dashboard") return
    if (!entityId || entityId === context.allEntitiesID) {
      navigate("/companies")
      return
    }
    if (parts[0] === "companies" && parts[1]) {
      const section = ["overview", "projects", "files", "reports", "employees"].includes(parts[2])
        ? parts[2]
        : "overview"
      navigate(`/companies/${entityId}/${section}`)
      return
    }
    if (["projects", "files", "reports"].includes(parts[0])) {
      navigate(`/companies/${entityId}/${parts[1] ? "overview" : parts[0]}`)
      return
    }
    if (!parts[0] || ["dashboard", "companies"].includes(parts[0])) {
      navigate(`/companies/${entityId}/overview`)
    }
  }

  const selectProject = (projectId: string) => {
    if (projectId !== context.state.projectId) resetDock()
    context.selectProject(projectId)
    const route = collectionRoute(location.pathname, location.search)
    if (route) {
      navigate(!projectId && route.page === "periods"
        ? collectionPath("projects", { companyID: context.state.entityId || undefined })
        : collectionPath(route.page, { companyID: context.state.entityId || undefined, projectID: projectId || undefined }))
      return
    }
    const parts = currentPath().split("/").filter(Boolean)
    if (parts[0] !== "projects" || !parts[1]) return
    if (!projectId) {
      navigate(`/companies/${context.state.entityId}/projects`)
      return
    }
    navigate(`/projects/${projectId}/${parts[2] || "overview"}`)
  }

  const selectPeriod = (periodId: string) => {
    context.selectPeriod(periodId)
    const route = collectionRoute(location.pathname, location.search)
    if (route && context.state.projectId) {
      navigate(collectionPath(route.page, {
        companyID: context.state.entityId, projectID: context.state.projectId,
        periodID: periodId === context.allPeriodsID ? undefined : periodId,
      }))
      return
    }
  }

  return (
    <div class="flex h-full min-h-0 w-full min-w-0 overflow-hidden bg-v2-background-bg-deep text-v2-text-text-base">
      <Show when={state.mobileNavigationOpen}>
        <button
          type="button"
          class="fixed inset-0 z-40 bg-v2-overlay-simple-overlay-scrim lg:hidden"
          aria-label={language.t("cowork.enterprise.nav.close")}
          onClick={() => setState("mobileNavigationOpen", false)}
        />
      </Show>

      <aside
        class="fixed inset-y-0 left-0 z-50 flex shrink-0 flex-col border-r border-v2-border-border-base bg-v2-background-bg-base transition-[width,transform] duration-200 lg:relative lg:translate-x-0"
        classList={{
          "w-[258px]": !state.sidebarCollapsed,
          "w-[72px]": state.sidebarCollapsed,
          "translate-x-0": state.mobileNavigationOpen,
          "-translate-x-full lg:translate-x-0": !state.mobileNavigationOpen,
        }}
      >
        <div
          class="flex h-[76px] items-center gap-3 border-b border-v2-border-border-base px-4"
          classList={{ "justify-center px-0": state.sidebarCollapsed }}
        >
          <Show when={!state.sidebarCollapsed}>
            <div class="min-w-0 flex-1">
              <CoworkBrand size={28} />
              <div class="mt-0.5 truncate text-[12px] text-v2-text-text-muted">{workspaceName() || language.t("cowork.enterprise.workspace.defaultName")}</div>
            </div>
          </Show>
          <button
            type="button"
            class="hidden size-9 shrink-0 items-center justify-center rounded-lg border border-v2-border-border-base text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover lg:flex"
            aria-label={language.t(state.sidebarCollapsed ? "cowork.navigation.expand" : "cowork.navigation.collapse")}
            onClick={() => setState("sidebarCollapsed", (value) => !value)}
          >
            <ShellIcon name="sidebar" class={state.sidebarCollapsed ? "rotate-180" : ""} />
          </button>
        </div>

        <div
          class="min-h-0 flex-1 px-3 py-4"
          classList={{
            "overflow-y-hidden": state.sidebarCollapsed,
            "overflow-y-auto": !state.sidebarCollapsed,
          }}
        >
          <For each={visibleSections()}>
            {(section) => (
              <div class="mb-5">
                <Show when={section.label && !state.sidebarCollapsed}>
                  <div class="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-v2-text-text-faint">
                    {language.t(section.label!)}
                  </div>
                </Show>
                <div class="space-y-1">
                  <For each={section.items}>
                    {(item) => (
                      <button
                        type="button"
                        class="flex h-10 w-full items-center gap-3 rounded-lg px-3 text-left text-[14px] text-v2-text-text-muted transition-colors hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                        classList={{
                          "bg-v2-background-bg-layer-03 !text-v2-text-text-base font-medium": active(item.route),
                          "justify-center px-0": state.sidebarCollapsed,
                        }}
                        title={state.sidebarCollapsed ? language.t(item.label) : undefined}
                        onClick={() => openRoute(item.route)}
                      >
                        <NavIcon name={item.icon} />
                        <Show when={!state.sidebarCollapsed}>
                          <span class="truncate">{language.t(item.label)}</span>
                        </Show>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            )}
          </For>
        </div>

        <div class="min-w-0 border-t border-v2-border-border-base p-3">
          <MenuV2 placement="top-start" gutter={8} flip={false} modal={false}>
            <MenuV2.Trigger
              class="flex w-full min-w-0 max-w-full items-center gap-3 overflow-hidden rounded-lg px-2 py-2 text-left hover:bg-v2-overlay-simple-overlay-hover"
              classList={{ "justify-center px-0": state.sidebarCollapsed }}
              aria-label={language.t("cowork.enterprise.profile.menu")}
              title={state.sidebarCollapsed ? accountName() : undefined}
            >
              <div class="grid size-9 shrink-0 place-items-center rounded-full bg-v2-background-bg-layer-03 text-[12px] font-semibold text-v2-text-text-base">
                {initials()}
              </div>
              <Show when={!state.sidebarCollapsed}>
                <div class="min-w-0 flex-1 overflow-hidden">
                  <div class="truncate text-[13px] font-medium" title={accountName()}>{accountName()}</div>
                  <div class="truncate text-[11px] text-v2-text-text-muted" title={auth.state.accountEmail}>{auth.state.accountEmail}</div>
                </div>
                <ShellIcon name="chevron" />
              </Show>
            </MenuV2.Trigger>
            <MenuV2.Portal>
              <MenuV2.Content class="min-w-[240px] overflow-hidden rounded-xl">
                <div class="flex items-center gap-3 px-3 py-3">
                  <div class="grid size-10 shrink-0 place-items-center rounded-full bg-v2-background-bg-layer-03 text-[12px] font-semibold text-v2-text-text-base">
                    {initials()}
                  </div>
                  <div class="min-w-0">
                    <div class="truncate text-[13px] font-semibold text-v2-text-text-base">{accountName()}</div>
                    <div class="truncate text-[11px] text-v2-text-text-muted">{auth.state.accountEmail}</div>
                  </div>
                </div>
                <MenuV2.Separator />
                <MenuV2.Item onSelect={() => void auth.logout()}>
                  <span class="flex items-center gap-2 text-v2-state-fg-danger">
                    <ShellIcon name="signout" />
                    {language.t("cowork.enterprise.profile.signOut")}
                  </span>
                </MenuV2.Item>
              </MenuV2.Content>
            </MenuV2.Portal>
          </MenuV2>
        </div>
      </aside>

      <div class="flex min-w-0 flex-1 flex-col">
        <header
          class="grid min-h-[76px] shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 border-b border-v2-border-border-base bg-v2-background-bg-base px-3 py-2 sm:px-5"
        >
          <div class="col-start-1 row-start-1 flex min-w-0 items-center gap-2">
            <button
              type="button"
              class="grid size-10 shrink-0 place-items-center rounded-lg text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover lg:hidden"
              aria-label={language.t("cowork.enterprise.nav.open")}
              onClick={() => setState("mobileNavigationOpen", true)}
            >
              <ShellIcon name="menu" />
            </button>

            <div class="hidden min-w-0 items-center gap-4 overflow-x-auto no-scrollbar md:flex">
              <Show
                when={!state.scopeSwitcherCollapsed}
                fallback={
                  <button
                    type="button"
                    class="flex h-10 max-w-[360px] items-center gap-2 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                    aria-label={language.t("cowork.enterprise.scope.expandSwitchers")}
                    title={language.t("cowork.enterprise.scope.expandSwitchers")}
                    onClick={toggleScopeSwitchers}
                  >
                    <span class="truncate">{scopeSummary()}</span>
                    <ShellIcon name="chevron" class="-rotate-90" />
                  </button>
                }
              >
                <ContextSelect
                  ariaLabel={language.t("cowork.company.label")}
                  value={context.state.entityId}
                  onChange={selectEntity}
                  placeholder={language.t("cowork.enterprise.scope.selectCompany")}
                  options={(context.companies() ?? []).map((company) => ({ value: company.id, label: company.name }))}
                />
                <Show when={!!context.state.entityId && !companyDetail()}>
                  <ContextSelect
                    ariaLabel={language.t("cowork.enterprise.scope.project")}
                    value={context.state.projectId}
                    onChange={selectProject}
                    placeholder={language.t("cowork.enterprise.scope.selectProject")}
                    options={availableProjects().map((project) => ({ value: project.id, label: project.name }))}
                  />
                </Show>
                <Show when={!companyDetail() && context.supportsPeriods() && availablePeriods().length > 0}>
                  <ContextSelect
                    ariaLabel={language.t("cowork.enterprise.scope.period")}
                    value={context.state.periodId}
                    onChange={(periodID) => { if (periodID !== context.state.periodId) resetDock(); selectPeriod(periodID) }}
                    placeholder={language.t("cowork.enterprise.scope.allPeriods")}
                    options={[
                      { value: context.allPeriodsID, label: language.t("cowork.enterprise.scope.allPeriods") },
                      ...availablePeriods().map((period) => ({
                        value: period.id,
                        label: formatDate(period.name),
                        badge: context.state.periodId === period.id ? language.t("cowork.enterprise.period.current") : undefined,
                      })),
                    ]}
                  />
                </Show>
                <button
                  type="button"
                  class="grid size-10 shrink-0 place-items-center rounded-lg border border-v2-border-border-base bg-v2-background-bg-base text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover"
                  aria-label={language.t("cowork.enterprise.scope.collapseSwitchers")}
                  title={language.t("cowork.enterprise.scope.collapseSwitchers")}
                  onClick={toggleScopeSwitchers}
                >
                  <ShellIcon name="chevron" class="rotate-90" />
                </button>
              </Show>
            </div>

            <button
              type="button"
              class="min-w-0 flex-1 truncate rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 py-2 text-left text-[13px] text-v2-text-text-muted md:hidden"
              onClick={() => setState("mobileNavigationOpen", true)}
            >
              {entityLabel()} <Show when={context.state.projectId}>/ {projectLabel()}</Show>
              <Show when={context.supportsPeriods() && context.state.periodId}> / {periodLabel()}</Show>
            </button>
          </div>

          <div class="col-start-2 row-start-1 ml-auto flex items-center gap-2">
            <button
              type="button"
              class="flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[12px] font-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
              data-chat-dock-toggle
              aria-label={language.t("cowork.chatDock.title")}
              title={language.t("cowork.chatDock.title")}
              aria-current={currentPath() === "/chats" ? "page" : undefined}
               onClick={() => {
                 closeDock()
                 navigate(collectionPath("chats", {
                   companyID: context.state.entityId || undefined,
                   projectID: context.state.projectId || undefined,
                   periodID: context.state.periodId === context.allPeriodsID ? undefined : context.state.periodId || undefined,
                 }))
                 window.dispatchEvent(new CustomEvent("cowork:chats-refresh"))
               }}
            >
              <ShellIcon name="chat" />
              <span class="hidden sm:inline">{language.t("cowork.chatDock.title")}</span>
            </button>
            <button
              type="button"
              class="hidden size-10 shrink-0 place-items-center rounded-lg text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover sm:grid"
              aria-label={language.t("cowork.enterprise.search.title")}
              title={language.t("cowork.enterprise.search.title")}
              onClick={() => setState({ searchOpen: true, searchError: "" })}
            >
              <ShellIcon name="search" />
            </button>
            <button
              type="button"
              class="hidden size-10 shrink-0 place-items-center rounded-lg text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover sm:grid"
              aria-label={language.t("cowork.enterprise.notifications")}
            >
              <ShellIcon name="bell" />
            </button>
            <button
              type="button"
              class="hidden size-10 place-items-center rounded-lg text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover sm:grid"
              aria-label={language.t("cowork.enterprise.help")}
            >
              <ShellIcon name="help" />
            </button>
          </div>
        </header>

        <div class="cowork-chat-workspace" classList={{ "dock-open": state.chatOpen }}><main class="min-h-0 min-w-0 flex-1 overflow-auto" hidden={state.chatOpen && state.chatExpanded} inert={state.chatOpen && mobileDock()}>
          {/* Header scope resources must not hold the router's tab-opening transition. */}
          <Suspense
            fallback={
              <div
                role="status"
                class="flex h-full items-center justify-center p-6 text-[13px] text-v2-text-text-muted"
              >
                {language.t("common.loading")}
              </div>
            }
          >
            <Show when={!isChatDockPath(location.pathname)}>{props.children}</Show>
          </Suspense>
        </main><Show when={state.chatPath}><CoworkChatDock path={state.chatPath} workspacePath={location.pathname} open={state.chatOpen} expanded={state.chatExpanded}
          sessions={chatTabs().map((tab) => ({ path: tabHref(tab), title: tabs.info[tabKey(tab)]?.title || language.t("cowork.enterprise.chats.untitled"),
            working: tabWorking(tab) }))}
          onSessionSelect={(path) => { const tab = chatTabs().find((item) => tabHref(item) === path); if (tab) selectChatTab(tab) }}
          onSessionClose={closeChatTab}
          onSessionReconnect={(path) => void reconnectChatTab(path)} reconnectingPath={state.reconnectingPath} reconnectError={state.reconnectError}
          onClose={closeDock} onNew={startChat} onExpand={() => setState("chatExpanded", !state.chatExpanded)}
          onPath={(path) => setState({ chatPath: path, chatScope: dockScope() })} onNavigate={(path) => { if (localWorkspacePath(path)) navigate(path) }} /></Show></div>
      </div>
      <Show when={state.searchOpen}>
        <Portal>
          <div
            class="fixed inset-0 z-[150] flex items-center justify-center bg-black/65 p-4 backdrop-blur-sm sm:p-8"
            role="presentation"
            onPointerDown={(event) => {
              if (event.target === event.currentTarget) setState("searchOpen", false)
            }}
          >
            <section
              class="flex h-[min(760px,90vh)] w-full max-w-[1120px] flex-col overflow-hidden rounded-[28px] border border-v2-border-border-base bg-v2-background-bg-layer-01 shadow-2xl"
              role="dialog"
              aria-modal="true"
              aria-label={language.t("cowork.enterprise.search.title")}
            >
              <header class="flex items-center gap-4 border-b border-v2-border-border-base px-6 py-5 sm:px-8">
                <ShellIcon name="search" />
                <input
                  ref={(element) => queueMicrotask(() => element.focus())}
                  class="h-11 min-w-0 flex-1 bg-transparent text-[24px] font-medium tracking-[-0.02em] text-v2-text-text-base outline-none placeholder:text-v2-text-text-faint"
                  value={state.searchQuery}
                  placeholder={language.t("cowork.enterprise.search.placeholder")}
                  aria-label={language.t("cowork.enterprise.search.title")}
                  onInput={(event) => setState("searchQuery", event.currentTarget.value)}
                />
                <button
                  type="button"
                  class="rounded-lg px-3 py-2 text-[12px] font-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base disabled:opacity-40"
                  disabled={!state.searchQuery}
                  onClick={() => setState("searchQuery", "")}
                >
                  {language.t("cowork.enterprise.search.clear")}
                </button>
                <span class="h-8 w-px bg-v2-border-border-base" />
                <button
                  type="button"
                  class="grid size-10 place-items-center rounded-lg text-v2-icon-icon-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                  aria-label={language.t("cowork.enterprise.search.close")}
                  title={language.t("cowork.enterprise.search.close")}
                  onClick={() => setState("searchOpen", false)}
                >
                  <ShellIcon name="close" />
                </button>
              </header>
              <div class="flex gap-2 px-6 py-4 sm:px-8">
                <For each={["all", "chats", "files"] as const}>
                  {(type) => (
                    <button
                      type="button"
                      class="rounded-full px-4 py-2 text-[13px] font-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover"
                      classList={{
                        "bg-v2-background-bg-layer-03 !text-v2-text-text-base": state.searchType === type,
                      }}
                      onClick={() => setState("searchType", type)}
                    >
                      {language.t(`cowork.enterprise.search.${type}` as keyof typeof import("@/i18n/en").dict)}
                    </button>
                  )}
                </For>
              </div>
              <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-6 sm:px-6">
                <Show when={state.searchError}>
                  <div class="mb-3 rounded-xl bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger">
                    {state.searchError}
                  </div>
                </Show>
                <Show
                  when={searchResults().length > 0}
                  fallback={
                    <div class="grid min-h-64 place-items-center text-[12px] text-v2-text-text-muted">
                      {chatIndex.loading || fileIndex.loading
                        ? language.t("common.loading")
                        : language.t("cowork.enterprise.search.empty")}
                    </div>
                  }
                >
                  <div class="space-y-1">
                    <For each={searchResults()}>
                      {(result) =>
                        result.type === "chats" ? (
                          <button
                            type="button"
                            class="flex w-full items-center gap-4 rounded-2xl px-4 py-3.5 text-left hover:bg-v2-overlay-simple-overlay-hover disabled:opacity-50"
                            disabled={!!state.searchOpening}
                            onClick={() => void openSearchChat(result.item)}
                          >
                            <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-v2-background-bg-layer-03 text-v2-icon-icon-muted">
                              <ShellIcon name="chat" />
                            </span>
                            <span class="min-w-0 flex-1">
                              <span class="block truncate text-[14px] font-medium text-v2-text-text-base">
                                {result.item.title}
                              </span>
                              <span class="mt-1 block truncate text-[11px] text-v2-text-text-muted">
                                {[result.item.project_name, result.item.period_name ?? result.item.workflow_name]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </span>
                            <span class="shrink-0 text-[10px] text-v2-text-text-faint">
                              {result.activity ? language.formatDateTime(result.activity) : ""}
                            </span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            class="flex w-full items-center gap-4 rounded-2xl px-4 py-3.5 text-left hover:bg-v2-overlay-simple-overlay-hover"
                            onClick={() => openSearchFile(result.item)}
                          >
                            <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-v2-background-bg-layer-03 text-v2-icon-icon-muted">
                              <NavIcon name="file" />
                            </span>
                            <span class="min-w-0 flex-1">
                              <span class="block truncate text-[14px] font-medium text-v2-text-text-base">
                                {result.item.filename}
                              </span>
                              <span class="mt-1 block truncate text-[11px] text-v2-text-text-muted">
                                {[result.item.project_name, result.item.period_name].filter(Boolean).join(" · ")}
                              </span>
                            </span>
                            <span class="shrink-0 text-[10px] text-v2-text-text-faint">
                              {result.activity ? language.formatDateTime(result.activity) : ""}
                            </span>
                          </button>
                        )
                      }
                    </For>
                  </div>
                </Show>
              </div>
            </section>
          </div>
        </Portal>
      </Show>
    </div>
  )
}

function ContextSelect(props: {
  ariaLabel: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  options: Array<{ value: string; label: string; badge?: string }>
}) {
  const selected = createMemo(() => props.options.find((option) => option.value === props.value))
  return (
    <MenuV2 placement="bottom-start" gutter={6} flip={false} sameWidth modal={false}>
      <MenuV2.Trigger
        class="flex h-10 min-w-[180px] max-w-[340px] shrink-0 items-center gap-2 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 text-left text-[13px] font-medium text-v2-text-text-base outline-none hover:border-v2-border-border-strong focus-visible:border-v2-border-border-focus"
        aria-label={props.ariaLabel}
        title={selected()?.label ?? props.placeholder}
      >
        <span class="shrink-0 whitespace-nowrap text-[11px] font-normal text-v2-text-text-muted">{props.ariaLabel}</span>
        <span class="min-w-0 flex-1 truncate">{selected()?.label ?? props.placeholder}</span>
        <Show when={selected()?.badge}>{(badge) => (
          <span class="shrink-0 rounded bg-v2-background-bg-layer-01 px-1.5 py-0.5 text-[10px] font-normal text-v2-text-text-muted">{badge()}</span>
        )}</Show>
        <ShellIcon name="chevron" />
      </MenuV2.Trigger>
      <MenuV2.Portal>
        <MenuV2.Content class="max-h-[320px] min-w-[var(--kb-popper-anchor-width)] overflow-y-auto">
          <For each={props.options}>
            {(option) => (
              <MenuV2.Item onSelect={() => props.onChange(option.value)}>
                <span class="flex min-w-0 items-center gap-2">
                  <span class="min-w-0 flex-1 truncate">{option.label}</span>
                  <Show when={option.badge}>{(badge) => (
                    <span class="shrink-0 rounded bg-v2-background-bg-layer-01 px-1.5 py-0.5 text-[10px] text-v2-text-text-muted">{badge()}</span>
                  )}</Show>
                  <Show when={option.value === props.value}>
                    <ShellIcon name="check" />
                  </Show>
                </span>
              </MenuV2.Item>
            )}
          </For>
        </MenuV2.Content>
      </MenuV2.Portal>
    </MenuV2>
  )
}

function storedScopeSwitcherCollapsed() {
  try {
    return localStorage.getItem(scopeSwitcherCollapsedKey) === "true"
  } catch {
    return false
  }
}

function chatActivity(chat: CoworkPortfolioChat) {
  const timestamp = Date.parse(chat.last_message_at || chat.updated_at || chat.created_at)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function fileActivity(file: CoworkPortfolioFile) {
  const timestamp = Date.parse(file.updated_at || file.created_at)
  return Number.isFinite(timestamp) ? timestamp : 0
}

function NavIcon(props: { name: NavIconName }) {
  const paths: Record<NavIconName, JSX.Element> = {
    dashboard: (
      <>
        <path d="M4 13a8 8 0 1 1 16 0" />
        <path d="m12 13 3-4" />
        <path d="M5 17h14" />
      </>
    ),
    tiles: (
      <>
        <rect x="3" y="3" width="8" height="8" rx="1" />
        <rect x="13" y="3" width="8" height="8" rx="1" />
        <rect x="3" y="13" width="8" height="8" rx="1" />
        <rect x="13" y="13" width="8" height="8" rx="1" />
      </>
    ),
    company: (
      <>
        <path d="M4 20V8l8-4v16" />
        <path d="M12 10h8v10" />
        <path d="M7 9h2M7 13h2M7 17h2M15 13h2M15 17h2" />
      </>
    ),
    project: (
      <>
        <path d="M3 7h7l2 2h9v11H3z" />
        <path d="M7 12v4M11 12v4" />
      </>
    ),
    chat: (
      <>
        <path d="M4 5h16v12H8l-4 4z" />
        <path d="M8 9h8M8 13h5" />
      </>
    ),
    file: (
      <>
        <path d="M6 3h8l4 4v14H6z" />
        <path d="M14 3v5h5M9 13h6M9 17h6" />
      </>
    ),
    folder: <path d="M3 6h7l2 2h9v12H3z" />,
    report: (
      <>
        <path d="M5 3h11l3 3v15H5z" />
        <path d="M9 17v-4M12 17V9M15 17v-6" />
      </>
    ),
    template: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M5 9h14M10 9v12M13 13h3M13 17h3" />
      </>
    ),
    ledger: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M8 3v18M11 8h7M11 12h7M11 16h5" />
      </>
    ),
    agent: (
      <>
        <rect x="5" y="7" width="14" height="11" rx="3" />
        <path d="M12 3v4M8 12h.01M16 12h.01M9 16h6" />
      </>
    ),
    library: (
      <>
        <rect x="4" y="4" width="6" height="6" rx="1" />
        <rect x="14" y="4" width="6" height="6" rx="1" />
        <rect x="4" y="14" width="6" height="6" rx="1" />
        <rect x="14" y="14" width="6" height="6" rx="1" />
      </>
    ),
    workflow: (
      <>
        <rect x="3" y="4" width="6" height="5" rx="1" />
        <rect x="15" y="15" width="6" height="5" rx="1" />
        <path d="M9 6.5h4a4 4 0 0 1 4 4V15M15 17.5h-4a4 4 0 0 1-4-4V9" />
      </>
    ),
    approval: (
      <>
        <rect x="5" y="4" width="14" height="17" rx="2" />
        <path d="M9 4V2h6v2M8 12l3 3 5-6" />
      </>
    ),
    activity: <path d="M3 13h4l2-7 4 13 2-6h6" />,
    integration: (
      <>
        <path d="M8 3v5M16 3v5M5 8h14v3a7 7 0 0 1-14 0zM12 18v3" />
      </>
    ),
    audit: (
      <>
        <path d="M4 5h12v14H4z" />
        <path d="M8 9h4M8 13h5M8 17h3" />
        <path d="m16 15 2 2 3-4" />
      </>
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19 12a7 7 0 0 0-.1-1l2-1.5-2-3.5-2.4 1A7 7 0 0 0 15 6l-.4-3h-5L9 6a7 7 0 0 0-1.5 1L5 6 3 9.5 5.1 11a7 7 0 0 0 0 2L3 14.5 5 18l2.5-1A7 7 0 0 0 9 18l.5 3h5l.5-3a7 7 0 0 0 1.5-1l2.5 1 2-3.5-2.1-1.5a7 7 0 0 0 .1-1z" />
      </>
    ),
  }
  return (
    <svg
      class="size-[19px] shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.7"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {paths[props.name]}
    </svg>
  )
}

function ShellIcon(props: {
  name: "sidebar" | "menu" | "search" | "bell" | "help" | "chat" | "chevron" | "plus" | "close" | "check" | "signout"
  class?: string
}) {
  const paths: Record<typeof props.name, JSX.Element> = {
    sidebar: (
      <>
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M9 4v16M15 9l-3 3 3 3" />
      </>
    ),
    menu: <path d="M4 7h16M4 12h16M4 17h16" />,
    search: (
      <>
        <circle cx="11" cy="11" r="6" />
        <path d="m16 16 4 4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    help: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9a2.5 2.5 0 1 1 3.1 2.4c-.6.2-.6.8-.6 1.6M12 17h.01" />
      </>
    ),
    chat: (
      <>
        <path d="M4 5h16v12H8l-4 4z" />
        <path d="M8 9h8M8 13h5" />
      </>
    ),
    chevron: <path d="m8 10 4 4 4-4" />,
    plus: <path d="M12 5v14M5 12h14" />,
    close: <path d="m7 7 10 10M17 7 7 17" />,
    check: <path d="m5 12 4 4 10-10" />,
    signout: (
      <>
        <path d="M10 5H5v14h5M14 8l4 4-4 4M9 12h9" />
      </>
    ),
  }
  return (
    <svg
      class={`size-[18px] shrink-0 ${props.class ?? ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {paths[props.name]}
    </svg>
  )
}
